/**
 * SPDX-FileCopyrightText: 2026 Nextcloud GmbH and Nextcloud contributors
 * SPDX-License-Identifier: AGPL-3.0-or-later
 */

/** Words per minute used for the reading estimate — the usual figure for prose. */
const WORDS_PER_MINUTE = 200

/**
 * Strips the markup so a word count counts words rather than syntax.
 *
 * Deliberately shallow: it drops the things that would otherwise be counted as
 * words — fenced code, link targets, image syntax, heading and list markers —
 * and leaves everything else alone.
 *
 * @param {string} content raw markdown
 * @return {string} text with the obvious markup removed
 */
function stripMarkup(content) {
	return content
		// fenced code blocks
		.replaceAll(/^```[\s\S]*?^```/gm, ' ')
		.replaceAll(/^~~~[\s\S]*?^~~~/gm, ' ')
		// images, which have no words to keep, then links, which keep their label.
		// Matched within a line: an unclosed bracket would otherwise scan the rest
		// of the note before failing, once per bracket
		.replaceAll(/!\[[^\]\n]*\]\([^)\n]*\)/g, ' ')
		.replaceAll(/\[([^\]\n]*)\]\([^)\n]*\)/g, '$1')
		// inline code, keeping its content
		.replaceAll(/`([^`]*)`/g, '$1')
		// heading, blockquote and list markers
		.replaceAll(/^\s{0,3}#{1,6}\s+/gm, '')
		.replaceAll(/^\s*>+\s?/gm, '')
		// \u00A0 escaped rather than literal: markdown-it-task-checkbox accepts a
		// non-breaking space inside the brackets, and a bare one here is invisible
		.replaceAll(/^\s*(?:[-+*]|\d+\.)\s+(?:\[[xX \u00A0]\]\s+)?/gm, '')
		// setext underlines and thematic breaks
		.replaceAll(/^\s*(?:={2,}|-{3,}|\*{3,}|_{3,})\s*$/gm, ' ')
		// emphasis markers, matched within a line: emphasis cannot span a blank
		// line, and an unclosed one would otherwise scan the note for a partner
		.replaceAll(/(\*{1,3}|_{1,3}|~~)(?=\S)([^\n]*?\S)\1/g, '$2')
}

/**
 * Counts for a note's body.
 *
 * @param {string} content raw markdown, may be empty or absent
 * @return {{words: number, readingMinutes: number}} counts
 */
export function noteTextStats(content) {
	const text = typeof content === 'string' ? content : ''
	const stripped = stripMarkup(text).trim()
	const words = stripped === '' ? 0 : stripped.split(/\s+/u).length

	return {
		words,
		// a note that has words never reads as zero minutes
		readingMinutes: words === 0 ? 0 : Math.max(1, Math.round(words / WORDS_PER_MINUTE)),
	}
}
